import React, { useMemo, useRef, useState, useEffect } from "react";

// Shared role label so the form can show "· Pastor / Limited Admin / …" next
// to each candidate. Mirrors the helper in KingdomConnect.jsx — keeping a copy
// here avoids a back-edge dependency from /appointments → KingdomConnect.
function roleLabel(role) {
  if (role === "superadmin") return "Super Admin";
  if (role === "limited_admin") return "Limited Admin";
  if (role === "pastor") return "Pastor";
  return "Team Member";
}

export default function AppointmentFormModal({ existing, currentUser, users, onSave, onClose }) {
  // Live member search (Point 10) — filter `users` by name as the user types.
  // The select used to enumerate every user; now we surface a search box with
  // a dropdown of matches so finding a member in a large directory is fast.
  const candidates = useMemo(
    () => (users || []).filter(u => u.id !== currentUser.id),
    [users, currentUser.id]
  );

  const initialPastor = existing && existing.pastorId
    ? candidates.find(u => u.id === existing.pastorId)
    : null;

  const [form, setForm] = useState(() => existing ? {
    pastorId: existing.pastorId || "",
    title: existing.title || "",
    reason: existing.reason || "",
    date: existing.date || new Date().toISOString().split("T")[0],
    time: existing.time || "10:00",
  } : { pastorId: "", title: "", reason: "", date: new Date().toISOString().split("T")[0], time: "10:00" });

  const [search, setSearch] = useState(initialPastor ? initialPastor.name : "");
  const [searchFocused, setSearchFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates.slice(0, 8);
    return candidates
      .filter(u => (u.name || "").toLowerCase().includes(q) || (u.username || "").toLowerCase().includes(q))
      .slice(0, 10);
  }, [candidates, search]);

  const selectedUser = candidates.find(u => u.id === form.pastorId) || null;

  const choosePastor = (u) => {
    set("pastorId", u.id);
    setSearch(u.name);
    setSearchFocused(false);
  };
  const clearPastor = () => {
    set("pastorId", "");
    setSearch("");
    setSearchFocused(true);
    if (searchRef.current) searchRef.current.focus();
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!searchFocused) return;
    const onDoc = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)
        && searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [searchFocused]);

  const submit = async () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      const ok = await onSave(form);
      if (ok !== false) { onClose(); return; }
    } catch (_) { /* parent already alerted */ }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="appt-form-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{existing && existing.id ? "Edit Appointment" : "New Appointment"}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group mb-16">
            <label>Title *</label>
            <input type="text" data-testid="appt-title" value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Pastoral counsel" autoFocus />
          </div>
          <div className="form-group mb-16">
            <label>Reason / Notes</label>
            <textarea data-testid="appt-reason" value={form.reason} onChange={e => set("reason", e.target.value)} rows={2} placeholder="Optional context…" />
          </div>
          <div className="form-grid mb-16">
            <div className="form-group">
              <label>Date *</label>
              <input type="date" data-testid="appt-date" value={form.date} onChange={e => set("date", e.target.value)} />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input type="time" data-testid="appt-time" value={form.time} onChange={e => set("time", e.target.value)} />
            </div>
          </div>
          <div className="form-group" style={{ position: "relative" }}>
            <label>With (Pastor / Team Member)</label>
            {selectedUser ? (
              <div data-testid="appt-pastor-selected" style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                padding: "10px 12px", background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)", borderRadius: 10,
                fontFamily: "'Montserrat',sans-serif",
              }}>
                <div>
                  <div style={{ color: "var(--kc-text)", fontSize: 14, fontWeight: 600 }}>{selectedUser.name}</div>
                  <div style={{ color: "var(--kc-sub)", fontSize: 11, marginTop: 2 }}>{roleLabel(selectedUser.role)}</div>
                </div>
                <button type="button" data-testid="appt-pastor-clear" onClick={clearPastor}
                  style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "var(--kc-sub)", borderRadius: 8, padding: "5px 10px", cursor: "pointer", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  ref={searchRef}
                  type="text"
                  data-testid="appt-pastor-search"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setSearchFocused(true); }}
                  onFocus={() => setSearchFocused(true)}
                  placeholder="Search pastor or team member by name…"
                  autoComplete="off"
                />
                {searchFocused && matches.length > 0 && (
                  <div
                    ref={dropdownRef}
                    data-testid="appt-pastor-dropdown"
                    style={{
                      position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
                      background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
                      borderRadius: 10, padding: 4, zIndex: 10, maxHeight: 220, overflowY: "auto",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                    }}
                  >
                    {matches.map(u => (
                      <div
                        role="button"
                        tabIndex={0}
                        key={u.id}
                        data-testid={`appt-pastor-opt-${u.id}`}
                        onClick={() => choosePastor(u)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choosePastor(u); } }}
                        style={{
                          width: "100%", textAlign: "left", background: "transparent",
                          border: "none", color: "var(--kc-text)", padding: "9px 10px",
                          borderRadius: 8, cursor: "pointer", fontFamily: "'Montserrat',sans-serif",
                          fontSize: 13,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "color-mix(in srgb, var(--kc-accent) 12%, transparent)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <div style={{ fontWeight: 600 }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>{roleLabel(u.role)}{u.username ? ` · ${u.username}` : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
                {searchFocused && matches.length === 0 && search.trim() && (
                  <div data-testid="appt-pastor-noresults" style={{
                    position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
                    background: "var(--kc-surface)", border: "1px solid rgba(255,107,107,0.30)",
                    borderRadius: 10, padding: 10, zIndex: 10,
                    color: "var(--kc-sub)", fontSize: 12, fontFamily: "'Montserrat',sans-serif",
                  }}>
                    No matches for &ldquo;{search}&rdquo;.
                  </div>
                )}
              </>
            )}
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
              Choosing a pastor sends them a confirmation request. Until they confirm, the appointment is Pending.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" data-testid="appt-save" disabled={!form.title.trim() || !form.date || saving} onClick={submit}>
            {saving ? "Saving…" : (existing && existing.id ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}
